import { formatDateTime, formatTime } from './format';
import { MAX_WINDOW_MS } from './history';

/** The longest period the picker accepts: what one time-range request can ask for. */
export const MAX_PERIOD_MS = MAX_WINDOW_MS;

const MINUTE = 60 * 1000;
const pad = (n: number) => String(n).padStart(2, '0');

/** "2026-09-30", local, as an `<input type="date">` value. */
export const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** "08:12", local, as an `<input type="time">` value. */
export const toTimeInput = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** The local instant an `<input type="date">` and `<input type="time">` pair names, if both are complete. */
export const parseLocal = (date: string, time: string): Date | undefined => {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})/.exec(time);
  if (!d || !t) return undefined;
  return new Date(+d[1], +d[2] - 1, +d[3], +t[1], +t[2]);
};

/**
 * The end of a period picked as a day and a minute: the minute is included, so "23:59" reaches the end of the day.
 */
export const endOfMinute = (d: Date) => new Date(d.getTime() + MINUTE - 1);

export type PeriodError = 'incomplete' | 'order' | 'future' | 'tooLong';

/** Why a picked period cannot be applied, or `undefined` when it can. */
export const validatePeriod = (from: Date | undefined, to: Date | undefined, now: Date): PeriodError | undefined => {
  if (!from || !to) return 'incomplete';
  if (from.getTime() >= to.getTime()) return 'order';
  if (from.getTime() > now.getTime()) return 'future';
  if (to.getTime() - from.getTime() > MAX_PERIOD_MS) return 'tooLong';
  return undefined;
};

/** "29 Sep 2026, 20:00 – 08:00" within one day, "29 Sep 2026, 20:00 – 30 Sep 2026, 08:00" across days. */
export const formatPeriod = (from: Date, to: Date) =>
  `${formatDateTime(from)} – ${from.toDateString() === to.toDateString() ? formatTime(to) : formatDateTime(to)}`;

/** "1 d 12 h", "45 min"; rounded to the minute. */
export const formatDuration = (ms: number) => {
  const minutes = Math.round(ms / MINUTE);
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  return [d ? `${d} d` : '', h ? `${h} h` : '', m ? `${m} min` : ''].filter(Boolean).join(' ') || '0 min';
};

/** The days of a month laid out in weeks starting on Monday; `null` pads the first and last week. */
export const monthCells = (year: number, month: number): Array<number | null> => {
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const total = Math.ceil((lead + days) / 7) * 7;
  return Array.from({ length: total }, (_, i) => {
    const day = i - lead + 1;
    return day >= 1 && day <= days ? day : null;
  });
};
